(function () {
  'use strict';

  var configPromises = new Map();
  var activeConfig = null;
  var membershipRequests = new Map();
  var membershipQueue = [];
  var membershipInFlight = 0;
  var storeWidePercent = 0;
  var priceSyncScheduled = false;
  var priceObserver = null;

  function numericId(value) {
    if (value == null) return '';
    var match = String(value).match(/(\d+)(?!.*\d)/);
    return match ? match[1] : '';
  }

  function clampPercent(value) {
    return Math.max(0, Math.min(100, Number(value || 0)));
  }

  function purchaseOptionMatches(offer, requestedMode) {
    var offerMode = String((offer && offer.purchaseOptionMode) || 'both');
    var mode = requestedMode === 'subscription' || requestedMode === 'one_time' ? requestedMode : 'both';
    return offerMode === 'both' || mode === 'both' || offerMode === mode;
  }

  function variantCollectionIds(config, variantId) {
    var id = numericId(variantId);
    var map = config && config.variantCollectionIdsByVariantId;
    var values = id && map && (map[id] || map[String(variantId || '')]);
    return Array.isArray(values) ? values.map(numericId).filter(Boolean) : [];
  }

  function normalizedConditions(conditions) {
    if (!conditions) {
      return { mode: 'all', items: [], collectionItems: [], minTotalQuantity: 0, minSubtotalCents: 0 };
    }
    if (Array.isArray(conditions)) {
      return { mode: 'all', items: conditions, collectionItems: [], minTotalQuantity: 0, minSubtotalCents: 0 };
    }
    return {
      mode: conditions.mode || 'all',
      items: Array.isArray(conditions.items) ? conditions.items : [],
      collectionItems: Array.isArray(conditions.collectionItems) ? conditions.collectionItems : [],
      minTotalQuantity: Number(conditions.minTotalQuantity || 0),
      minSubtotalCents: Number(conditions.minSubtotalCents || 0)
    };
  }

  function cartFromLines(config, lines) {
    var grouped = new Map();
    (lines || []).forEach(function (line) {
      var id = numericId(line && (line.variant_id || line.id));
      if (!id) return;
      var existing = grouped.get(id) || { variant_id: id, quantity: 0, price: Number(line.price || 0), collectionIds: line.collectionIds || [] };
      existing.quantity += Math.max(1, Number(line.quantity || 1));
      if (!existing.price) existing.price = Number(line.price || 0);
      grouped.set(id, existing);
    });
    return {
      items: Array.from(grouped.values()).map(function (item) {
        item.collectionIds = Array.from(new Set(item.collectionIds.concat(variantCollectionIds(config, item.variant_id)).map(numericId).filter(Boolean)));
        item.line_price = item.price * item.quantity;
        item.final_line_price = item.line_price;
        return item;
      })
    };
  }

  function collectionMatches(offer, item, collectionId) {
    var id = numericId(collectionId);
    if ((item.collectionIds || []).map(numericId).indexOf(id) < 0) return false;
    var scopes = offer.variantScopedCollections || [];
    for (var i = scopes.length - 1; i >= 0; i--) {
      if (numericId(scopes[i].collectionId) === id) {
        return (scopes[i].variantIds || []).map(numericId).indexOf(numericId(item.variant_id)) >= 0;
      }
    }
    return true;
  }

  function matchesMerchandise(offer, item, variants, collections) {
    if (!variants.length && !collections.length) return true;
    return variants.map(numericId).indexOf(numericId(item.variant_id)) >= 0 || collections.some(function (id) {
      return collectionMatches(offer, item, id);
    });
  }

  function merchandiseTargets(offer, pageOffer) {
    var conditions = normalizedConditions(offer.conditions);
    var variants = (offer.targets || []).concat(offer.targetVariantIds || []);
    var collections = (offer.collectionTargets || []).concat(offer.targetCollectionIds || []);
    if (pageOffer || (!variants.length && !collections.length)) {
      variants = variants.concat(conditions.items.map(function (item) { return item.variantId; }));
      collections = collections.concat(conditions.collectionItems.map(function (item) { return item.collectionId; }));
    }
    return { variants: variants, collections: collections };
  }

  function qualifyingQuantity(offer, cart) {
    var conditions = normalizedConditions(offer.conditions);
    var variants = conditions.items.map(function (item) { return item.variantId; });
    var collections = conditions.collectionItems.map(function (item) { return item.collectionId; });
    if (!variants.length && !collections.length) {
      var targets = merchandiseTargets(offer, false);
      variants = targets.variants;
      collections = targets.collections;
    }
    return (cart.items || []).reduce(function (total, item) {
      return total + (matchesMerchandise(offer, item, variants, collections) ? Number(item.quantity || 0) : 0);
    }, 0);
  }

  function eligible(offer, cart, purchaseOptionMode) {
    if (!purchaseOptionMatches(offer, purchaseOptionMode)) return false;
    var conditions = normalizedConditions(offer && offer.conditions);
    var items = cart.items || [];
    var subtotal = items.reduce(function (total, item) { return total + Number(item.line_price || 0); }, 0);
    if (conditions.minSubtotalCents > 0 && subtotal < conditions.minSubtotalCents) return false;
    if (conditions.mode === 'any_total' && qualifyingQuantity(offer, cart) < (conditions.minTotalQuantity || 1)) return false;
    if (conditions.mode === 'any_total') return true;
    return conditions.items.every(function (condition) {
      return items.reduce(function (total, item) {
        return total + (numericId(item.variant_id) === numericId(condition.variantId) ? item.quantity : 0);
      }, 0) >= Number(condition.minQuantity || condition.quantity || 1);
    }) && conditions.collectionItems.every(function (condition) {
      return items.reduce(function (total, item) {
        return total + (collectionMatches(offer, item, condition.collectionId) ? item.quantity : 0);
      }, 0) >= Number(condition.minQuantity || condition.quantity || 1);
    });
  }

  function tierPercent(tier) {
    return clampPercent(
      tier && (
        tier.percentOff ||
        tier.discountPercent ||
        (tier.rewards && tier.rewards.discountPercent)
      )
    );
  }

  function bestTierPercent(offer, cart, pageOffer) {
    var quantity = qualifyingQuantity(offer, cart);
    var rewardTiers = hasProductRewardTiers(offer);
    if (!pageOffer && rewardTiers) {
      var targets = merchandiseTargets(offer, false);
      quantity = (cart.items || []).reduce(function (sum, item) {
        return sum + (matchesMerchandise(offer, item, targets.variants, targets.collections) ? item.quantity : 0);
      }, 0);
    }
    var reached = (offer.tiers || []).filter(function (tier) {
      return quantity >= Math.max(1, Number(tier.minQuantity || 1));
    });
    if (pageOffer || rewardTiers) {
      reached.sort(function (a, b) { return Number(b.minQuantity) - Number(a.minQuantity) || tierPercent(b) - tierPercent(a); });
      // A uniform price preview cannot represent a capped free-unit allocation.
      if (reached[0] && reached[0].rewards && reached[0].rewards.freeItems) return 0;
      return reached.length ? tierPercent(reached[0]) : 0;
    }
    return reached.reduce(function (best, tier) { return Math.max(best, tierPercent(tier)); }, 0);
  }

  function normalizePath(path) {
    var value = String(path || '/').split('?')[0].toLowerCase();
    if (!value.startsWith('/')) value = '/' + value;
    return value.length > 1 && value.endsWith('/') ? value.slice(0, -1) : value;
  }

  function pageRuleMatches(rule, locationLike) {
    if (!rule) return false;
    var raw = String(rule).trim().toLowerCase();
    var queryIndex = raw.indexOf('?');
    var rulePath = queryIndex >= 0 ? raw.slice(0, queryIndex) : raw;
    var ruleQuery = queryIndex >= 0 ? raw.slice(queryIndex + 1) : '';
    var isPrefix = rulePath.endsWith('*');
    if (isPrefix) rulePath = rulePath.slice(0, -1);
    var currentPath = normalizePath(locationLike.pathname);
    var expectedPath = normalizePath(rulePath);
    var pathMatches = isPrefix
      ? currentPath === expectedPath || currentPath.startsWith(expectedPath + '/')
      : currentPath === expectedPath;
    if (!pathMatches) return false;

    var expected = new URLSearchParams(ruleQuery);
    var expectedView = String(expected.get('view') || '').trim().toLowerCase();
    if (!expectedView) return true;
    var current = new URLSearchParams(locationLike.search || '');
    return String(current.get('view') || '').trim().toLowerCase() === expectedView;
  }

  function activePageOfferIds(config, locationLike) {
    return new Set((Array.isArray(config && config.pageOffers) ? config.pageOffers : [])
      .filter(function (offer) {
        return offer && offer.id && (offer.storeWide === true || (Array.isArray(offer.pagePaths) && offer.pagePaths.some(function (rule) {
          return pageRuleMatches(rule, locationLike);
        })));
      })
      .map(function (offer) { return String(offer.id); }));
  }

  function productOfferPercent(offer, variant, cart, purchaseOptionMode, config) {
    if (!offer || offer.freeItemReward || offer.recommendationOnly || !purchaseOptionMatches(offer, purchaseOptionMode)) return 0;
    var variantId = numericId(variant && variant.id);
    if (!variantId) return 0;
    var targets = merchandiseTargets(offer, false);
    var item = (cart.items || []).find(function (entry) { return numericId(entry.variant_id) === variantId; });
    if (!item || !matchesMerchandise(offer, item, targets.variants, targets.collections) || !eligible(offer, cart, purchaseOptionMode)) return 0;
    var tier = bestTierPercent(offer, cart);
    if (tier > 0 || hasProductRewardTiers(offer)) return tier;
    if (String(offer.valueType || 'percentage') === 'fixed') {
      var price = Number(variant.price || 0);
      return price > 0 ? clampPercent((Number(offer.value || 0) * 100 / price) * 100) : 0;
    }
    return clampPercent(offer.value);
  }

  function pageOfferPercent(offer, variant, cart, pageOfferIds, purchaseOptionMode) {
    if (!offer || !pageOfferIds.has(String(offer.id))) return 0;
    var targets = merchandiseTargets(offer, true);
    var item = (cart.items || []).find(function (entry) { return numericId(entry.variant_id) === numericId(variant.id); });
    if (!item || !matchesMerchandise(offer, item, targets.variants, targets.collections)) return 0;
    if (!eligible(offer, cart, purchaseOptionMode)) return 0;
    return bestTierPercent(offer, cart, true);
  }

  function quote(config, lines, options) {
    var safeConfig = config || {};
    var purchaseOptionMode = (options && options.purchaseOptionMode) || 'one_time';
    var locationLike = (options && options.location) || window.location;
    var cart = cartFromLines(safeConfig, lines);
    var pageOfferIds = activePageOfferIds(safeConfig, locationLike);
    var quotedLines = cart.items.map(function (item) {
      var variant = { id: item.variant_id, price: item.price };
      var percent = 0;
      (Array.isArray(safeConfig.productOffers) ? safeConfig.productOffers : []).forEach(function (offer) {
        percent = Math.max(percent, productOfferPercent(offer, variant, cart, purchaseOptionMode, safeConfig));
      });
      (Array.isArray(safeConfig.pageOffers) ? safeConfig.pageOffers : []).forEach(function (offer) {
        percent = Math.max(percent, pageOfferPercent(offer, variant, cart, pageOfferIds, purchaseOptionMode));
      });
      var regularTotal = Math.round(Number(item.price || 0) * Number(item.quantity || 0));
      return {
        id: item.variant_id,
        quantity: item.quantity,
        discountPercent: clampPercent(percent),
        regularTotal: regularTotal,
        discountedTotal: discountedTotal(item.price, percent) * item.quantity
      };
    });
    return {
      discountPercent: quotedLines.reduce(function (best, line) {
        return Math.max(best, line.discountPercent);
      }, 0),
      regularTotal: quotedLines.reduce(function (total, line) { return total + line.regularTotal; }, 0),
      discountedTotal: quotedLines.reduce(function (total, line) { return total + line.discountedTotal; }, 0),
      lines: quotedLines,
      activePageOfferIds: Array.from(pageOfferIds)
    };
  }

  function fetchConfig(productHandle, options) {
    var handle = String(productHandle || '').trim();
    var cacheKey = handle || '__global__';
    var fresh = Boolean(options && options.fresh);
    if (!fresh && configPromises.has(cacheKey)) return configPromises.get(cacheKey);
    var shop = document.documentElement.dataset.revliftShopDomain || (window.Shopify && window.Shopify.shop) || '';
    var url = new URL('/apps/everything-app', window.location.origin);
    if (shop) url.searchParams.set('shop', shop);
    if (handle) url.searchParams.set('product_handle', handle);
    if (fresh) url.searchParams.set('_theme_pricing', String(Date.now()));
    var promise = fetch(url.toString(), {
      cache: fresh ? 'no-store' : 'default',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    }).then(function (response) {
      if (!response.ok) throw new Error('EA pricing config request failed.');
      return response.json();
    }).then(function (payload) {
      return (payload && payload.config) || {};
    }).catch(function (error) {
      if (!fresh) configPromises.delete(cacheKey);
      throw error;
    });
    if (!fresh) configPromises.set(cacheKey, promise);
    return promise;
  }

  function hasProductRewardTiers(offer) {
    return (offer.tiers || []).some(function (tier) { return Boolean(tier.rewards); });
  }

  function offerHasDiscount(offer) {
    if (!offer || offer.recommendationOnly) return false;
    if (offer.freeItemReward) return true;
    if (clampPercent(offer.value) > 0 && String(offer.valueType || 'percentage') !== 'fixed') return true;
    if (Number(offer.value || 0) > 0 && String(offer.valueType || '') === 'fixed') return true;
    return (Array.isArray(offer.tiers) ? offer.tiers : []).some(function (tier) {
      var rewards = tier.rewards || {};
      return tierPercent(tier) > 0 || Boolean(rewards.freeItems || rewards.freeGift || rewards.freeShipping);
    });
  }

  function storeWideBannerOffers(config) {
    // The proxy supplies active offers. Announcement visibility is independent
    // of whether the current product/cart qualifies for the advertised offer.
    var seen = new Set();
    return ['productOffers', 'pageOffers', 'orderOffers', 'shippingOffers', 'giftOffers'].reduce(function (offers, group) {
      return offers.concat(((config && config[group]) || []).filter(function (offer) {
        if (!offer || offer.storeWide !== true || offer.recommendationOnly ||
            !(offerHasDiscount(offer) || group === 'shippingOffers' || (group === 'giftOffers' && offer.giftVariantId))) return false;
        if (offer.id && seen.has(offer.id)) return false;
        if (offer.id) seen.add(offer.id);
        return true;
      }));
    }, []);
  }

  function hasStoreWideDiscount(config) {
    return storeWideBannerOffers(config).length > 0;
  }

  function storeWideBannerPercent(config) {
    // Advertised maximum for the editor's [percent] token, never a price input.
    return storeWideBannerOffers(config).reduce(function (best, offer) {
      var percent = offer.freeItemReward || hasProductRewardTiers(offer) || String(offer.valueType || 'percentage') === 'fixed' ? 0 : clampPercent(offer.value);
      return Math.max(best, percent, (offer.tiers || []).reduce(function (tierBest, tier) {
        return Math.max(tierBest, tierPercent(tier));
      }, 0));
    }, 0);
  }

  function storeWideDiscountPercent(config, options) {
    var settings = options || {};
    var lines = settings.lines || (settings.variantId ? [{ id: settings.variantId, quantity: settings.quantity || 1,
      price: Number(settings.subtotalCents || 0) / Math.max(1, Number(settings.quantity || 1)), collectionIds: settings.collectionIds || [] }] : []);
    var cart = cartFromLines(config, lines);
    var groups = ['productOffers', 'pageOffers', 'orderOffers'];
    return groups.reduce(function (best, group) {
      return ((config && config[group]) || []).reduce(function (offerBest, offer) {
        if (!offer || offer.freeItemReward || offer.storeWide !== true || !offerHasDiscount(offer)) return offerBest;
        var conditions = normalizedConditions(offer.conditions);
        var targets = merchandiseTargets(offer, group === 'pageOffers');
        var hasRestrictions = targets.variants.length || targets.collections.length || conditions.items.length || conditions.collectionItems.length;
        // An unidentified price cannot establish merchandise eligibility.
        if (!cart.items.length && hasRestrictions) return offerBest;
        var candidateCart = cart.items.length ? cart : { items: [{ variant_id: '', collectionIds: [],
          quantity: Math.max(1, Number(settings.quantity || 1)), line_price: Number(settings.subtotalCents || 0) }] };
        if (!eligible(offer, candidateCart, settings.purchaseOptionMode || 'one_time')) return offerBest;
        // Aggregate rates must never spread a selected-product discount across a mixed bundle.
        if (group !== 'orderOffers' && !candidateCart.items.every(function (item) {
          return matchesMerchandise(offer, item, targets.variants, targets.collections);
        })) return offerBest;
        var valuePercent = String(offer.valueType || 'percentage') === 'fixed' ? 0 : clampPercent(offer.value);
        var tier = bestTierPercent(offer, candidateCart, group === 'pageOffers');
        return Math.max(offerBest, group === 'pageOffers' || hasProductRewardTiers(offer) ? tier : (tier || valuePercent));
      }, best);
    }, 0);
  }

  function formatPercent(value) {
    var rounded = Math.round(Number(value || 0) * 100) / 100;
    return String(rounded);
  }

  function pricingLinesForElement(element, options) {
    var settings = options || {};
    if (!element || !element.querySelector) return [];
    var scope = element.closest('product-info, [data-offer-cta], [data-new-pdp-cta]') || element;
    var form = element.matches('form') ? element : scope.querySelector('form[action*="/cart/add"]');
    var handle = element.getAttribute('data-revlift-product-handle') || settings.productHandle || '';
    var path = scope.getAttribute('data-url') || window.location.pathname;
    if (!handle && /\/products\//.test(path)) handle = path.split('/products/')[1].split(/[/?#]/)[0];
    var id = settings.variantId || element.getAttribute('data-revlift-variant-id');
    var result = [];
    if (!id && form) {
      var fields = new FormData(form);
      fields.forEach(function (value, name) {
        var match = name.match(/^items\[(\d+)\]\[id\]$/);
        if (match) result.push({ id: value, quantity: Number(fields.get('items[' + match[1] + '][quantity]') || 1), productHandle: handle });
      });
      if (!result.length) id = fields.get('id');
    }
    if (!result.length && id) result.push({ id: id, quantity: Number(settings.quantity || (form && new FormData(form).get('quantity')) || 1), productHandle: handle });
    var totalQty = result.reduce(function (total, line) { return total + line.quantity; }, 0);
    result.forEach(function (line) { line.price = Number(settings.subtotalCents || 0) / Math.max(1, totalQty); });
    // Liquid knows card membership already; avoid a proxy request per grid item.
    var renderedCollections = element.getAttribute('data-revlift-collection-ids');
    if (id && renderedCollections != null) {
      try {
        var collectionIds = JSON.parse(renderedCollections);
        if (Array.isArray(collectionIds)) result.forEach(function (line) {
          line.collectionIds = collectionIds;
          line.membershipKnown = true;
        });
      } catch (error) { /* Invalid markup falls back to verified membership lookup. */ }
    }
    return result;
  }

  function ensurePricingMembership(config, lines) {
    if (!config || config !== activeConfig) return;
    var collectionOffers = ['productOffers', 'pageOffers', 'orderOffers'].some(function (key) {
      return (config[key] || []).some(function (offer) {
        return offer.storeWide === true && (merchandiseTargets(offer, true).collections.length > 0);
      });
    });
    if (!collectionOffers) return;
    (lines || []).forEach(function (line) {
      var id = numericId(line.id || line.variant_id);
      var membership = config.variantCollectionIdsByVariantId || {};
      var handle = String(line.productHandle || '').trim();
      if (!id || !handle || line.membershipKnown || Object.prototype.hasOwnProperty.call(membership, id) || membershipRequests.has(handle)) return;
      membershipRequests.set(handle, true);
      membershipQueue.push({ config: config, handle: handle });
    });
    drainMembershipQueue();
  }

  function drainMembershipQueue() {
    // Collection grids can contain many cards; share requests and bound concurrency.
    while (membershipInFlight < 4 && membershipQueue.length) {
      var task = membershipQueue.shift();
      if (task.config !== activeConfig) continue;
      membershipInFlight++;
      (function (request) {
        fetchConfig(request.handle).then(function (productConfig) {
          if (request.config !== activeConfig) return;
          request.config.variantCollectionIdsByVariantId = Object.assign({}, request.config.variantCollectionIdsByVariantId,
            productConfig.variantCollectionIdsByVariantId || {});
          schedulePriceSync();
          document.dispatchEvent(new CustomEvent('revlift:storewide-state', { detail: { active: window.RevLiftPricing.storeWideActive } }));
        }).catch(function () { /* Unknown membership stays ineligible until a fresh configuration load. */ }).finally(function () {
          membershipInFlight--;
          drainMembershipQueue();
        });
      })(task);
    }
  }

  function activeStoreWidePercent(options) {
    var settings = typeof options === 'string' ? { purchaseOptionMode: options } : Object.assign({}, options || {});
    if (!activeConfig) return 0;
    if (!settings.lines && settings.element) settings.lines = pricingLinesForElement(settings.element, settings);
    if (!settings.lines && settings.variantId) settings.lines = [{ id: settings.variantId,
      quantity: settings.quantity || 1, productHandle: settings.productHandle,
      price: Number(settings.subtotalCents || 0) / Math.max(1, Number(settings.quantity || 1)), collectionIds: settings.collectionIds || [] }];
    ensurePricingMembership(activeConfig, settings.lines);
    return clampPercent(storeWideDiscountPercent(activeConfig, settings));
  }

  function bestDiscountPercent(currentPercent, options) {
    return Math.max(clampPercent(currentPercent), activeStoreWidePercent(options));
  }

  function discountedTotal(baseTotal, percent) {
    var base = Math.max(0, Math.round(Number(baseTotal || 0)));
    // Shopify truncates the discount per unit, then multiplies by quantity.
    return Math.max(0, base - Math.floor(base * clampPercent(percent) / 100 + 1e-7));
  }

  function bestDiscountedTotal(baseTotal, currentTotal, options) {
    var base = Math.max(0, Math.round(Number(baseTotal || 0)));
    var current = Number(currentTotal);
    if (!Number.isFinite(current) || current < 0) current = base;
    var pricingOptions = Object.assign({
      purchaseOptionMode: 'one_time',
      subtotalCents: base
    }, options || {});
    return Math.min(Math.round(current), discountedTotal(base, activeStoreWidePercent(pricingOptions)));
  }

  function currencyCode() {
    return String(
      document.documentElement.dataset.revliftCurrency ||
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
      'USD'
    );
  }

  function formatMoney(cents) {
    var locale = document.documentElement.lang || 'en-US';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency: currencyCode() })
        .format(Number(cents || 0) / 100);
    } catch (error) {
      return '$' + (Number(cents || 0) / 100).toFixed(2);
    }
  }

  function parseMoneyToCents(text) {
    var value = String(text || '').replace(/[^0-9.,-]/g, '');
    if (!value) return 0;
    var lastComma = value.lastIndexOf(',');
    var lastDot = value.lastIndexOf('.');
    if (lastComma > lastDot) {
      value = value.replace(/\./g, '').replace(',', '.');
    } else {
      value = value.replace(/,/g, '');
    }
    var amount = Number(value);
    return Number.isFinite(amount) ? Math.max(0, Math.round(amount * 100)) : 0;
  }

  function syncPriceSurface(surface) {
    var original = surface.querySelector('[data-revlift-original-price]');
    var sale = surface.querySelector('[data-revlift-sale-price]');
    var saleValue = surface.querySelector('[data-revlift-sale-value]');
    var saleCompare = surface.querySelector('[data-revlift-sale-compare]');
    if (!original || !sale || !saleValue || !saleCompare) return;

    var base = Number(surface.getAttribute('data-revlift-base-cents') || 0);
    var surfaceOptions = { element: surface, purchaseOptionMode: 'one_time', quantity: 1, subtotalCents: base };
    var percent = activeStoreWidePercent(surfaceOptions);
    var active = percent > 0 && base > 0;
    original.hidden = active;
    sale.hidden = !active;
    if (surface.__revliftOriginalOnSale == null) surface.__revliftOriginalOnSale = surface.classList.contains('price--on-sale');
    surface.classList.toggle('price--on-sale', active || surface.__revliftOriginalOnSale);
    if (!active) return;

    var discountedText = formatMoney(discountedTotal(base, percent));
    var compareText = formatMoney(base);
    if (saleValue.textContent !== discountedText) saleValue.textContent = discountedText;
    if (saleCompare.textContent !== compareText) saleCompare.textContent = compareText;
  }

  function syncGenericProductPrice(node) {
    if (node.closest('[data-revlift-price], [data-revlift-subscription-widget], [data-offer-cta], [data-new-pdp-cta]')) return;

    if (!node.__revliftOriginalText) {
      node.__revliftOriginalText = String(node.textContent || '').trim();
      node.__revliftBaseCents = parseMoneyToCents(node.__revliftOriginalText);
    }

    var currentText = String(node.textContent || '').trim();
    if (currentText !== (node.__revliftRenderedText || node.__revliftOriginalText)) {
      node.__revliftOriginalText = currentText;
      node.__revliftBaseCents = parseMoneyToCents(currentText);
    }

    var percent = activeStoreWidePercent({
      element: node,
      purchaseOptionMode: 'one_time',
      quantity: 1,
      subtotalCents: node.__revliftBaseCents
    });
    var active = percent > 0;
    if (active && node.__revliftRenderedText && String(node.textContent || '').trim() !== node.__revliftRenderedText) {
      node.__revliftOriginalText = String(node.textContent || '').trim();
      node.__revliftBaseCents = parseMoneyToCents(node.__revliftOriginalText);
      percent = activeStoreWidePercent({
        element: node,
        purchaseOptionMode: 'one_time',
        quantity: 1,
        subtotalCents: node.__revliftBaseCents
      });
      active = percent > 0;
    }

    var compareAt = node.parentElement && node.parentElement.querySelector
      ? node.parentElement.querySelector('[data-product-type="compare_at_price"]')
      : null;
    if (compareAt && compareAt !== node) {
      if (compareAt.__revliftOriginalHidden == null) compareAt.__revliftOriginalHidden = compareAt.hidden;
      compareAt.hidden = active || compareAt.__revliftOriginalHidden;
    }

    if (!active || !(node.__revliftBaseCents > 0)) {
      if (node.__revliftRenderedText) node.textContent = node.__revliftOriginalText;
      node.__revliftRenderedText = '';
      return;
    }

    if (node.__revliftRenderedText && String(node.textContent || '').trim() === node.__revliftRenderedText && node.__revliftRenderedPercent === percent) return;
    node.__revliftRenderedPercent = percent;

    var prefix = /\bfrom\b/i.test(node.__revliftOriginalText) ? 'From ' : '';
    var discounted = formatMoney(discountedTotal(node.__revliftBaseCents, percent));
    var regular = formatMoney(node.__revliftBaseCents);
    node.innerHTML = '';
    if (prefix) node.appendChild(document.createTextNode(prefix));
    var current = document.createElement('span');
    current.className = 'revlift-storewide-price';
    current.textContent = discounted;
    var compare = document.createElement('s');
    compare.className = 'revlift-storewide-compare';
    compare.textContent = regular;
    node.appendChild(current);
    node.appendChild(document.createTextNode(' '));
    node.appendChild(compare);
    node.__revliftRenderedText = String(node.textContent || '').trim();
  }

  function syncPurchaseOptionForm(form, publishedLocally) {
    if (!form || !form.hasAttribute('data-revlift-base-total-cents')) return false;
    var base = Number(form.getAttribute('data-revlift-base-total-cents') || 0);
    var published = Number(form.getAttribute('data-revlift-one-time-total-cents') || base);
    if (!(base > 0)) return false;
    if (publishedLocally || !form.hasAttribute('data-revlift-local-one-time-total-cents')) {
      form.setAttribute('data-revlift-local-one-time-total-cents', String(published));
    }
    var local = Number(form.getAttribute('data-revlift-local-one-time-total-cents') || base);
    var best = bestDiscountedTotal(base, local, { element: form, purchaseOptionMode: 'one_time' });
    var changed = best !== published;
    form.setAttribute('data-revlift-one-time-total-cents', String(best));
    form.setAttribute('data-revlift-compare-total-cents', String(base));
    return changed;
  }

  function syncStoreWidePricing() {
    document.querySelectorAll('[data-revlift-price]').forEach(syncPriceSurface);
    document.querySelectorAll('[data-product-price="true"]').forEach(syncGenericProductPrice);
    document.querySelectorAll('form[data-revlift-base-total-cents]').forEach(function (form) { syncPurchaseOptionForm(form); });
  }

  function schedulePriceSync() {
    if (priceSyncScheduled) return;
    priceSyncScheduled = true;
    window.requestAnimationFrame(function () {
      priceSyncScheduled = false;
      syncStoreWidePricing();
    });
  }

  function observeDynamicPrices() {
    if (priceObserver || !window.MutationObserver || !document.body) return;
    priceObserver = new MutationObserver(schedulePriceSync);
    priceObserver.observe(document.body, { childList: true, subtree: true });
  }

  function refreshPurchaseOptionForms() {
    document.querySelectorAll('form[data-revlift-base-total-cents]').forEach(function (form) {
      syncPurchaseOptionForm(form);
      form.dispatchEvent(new CustomEvent('revlift:refresh', {
        bubbles: true,
        detail: { source: 'storewide-pricing' }
      }));
    });
  }

  function safeBannerDestination(value) {
    if (typeof value !== 'string') return '';
    var url = value.trim();
    if (!url || url.length > 2048 || /[\\\u0000-\u0020\u007f]/.test(url)) return '';
    if (url.charAt(0) === '/' && url.charAt(1) !== '/') return url;
    try {
      var parsed = new URL(url);
      if (/^https?:\/\//i.test(url) && !parsed.username && !parsed.password) return url;
    } catch (error) { /* Invalid saved metadata falls back to the theme link. */ }
    return '';
  }

  function syncSaleBannerText(banners, config) {
    var percent = storeWideBannerPercent(config);
    var offers = storeWideBannerOffers(config);
    banners.forEach(function (banner) {
      var message = banner.querySelector('[data-revlift-sale-message]');
      if (!message) return;
      var wrapper = message.closest('.revlift-sale-banner__link');
      var fallback = safeBannerDestination(wrapper && (wrapper.getAttribute('data-revlift-sale-fallback-url') || wrapper.getAttribute('href'))) || '/collections/all';
      // Also tolerate the older section markup during a theme asset rollout.
      if (wrapper && wrapper.tagName === 'A') {
        var container = document.createElement('div');
        container.className = wrapper.className;
        container.setAttribute('data-revlift-sale-fallback-url', fallback);
        wrapper.replaceWith(container);
        container.appendChild(message);
      }
      var mode = message.getAttribute('data-revlift-sale-message-source') || 'offer_titles';
      var template = message.getAttribute('data-revlift-sale-message-template') || 'Save [percent]% Storewide';
      var automatic = mode === 'offer_titles' || (percent <= 0 && /\[\s*percent\s*\]|\{\{\s*percent\s*\}\}/i.test(template));
      var entries = automatic ? offers.map(function (offer) {
        return { text: String(offer.title || '').trim() || 'Special offers available', href: safeBannerDestination(offer.bannerDestinationUrl) || fallback };
      }) : [{ text: template.replace(/\[\s*percent\s*\]/gi, formatPercent(percent)).replace(/\{\{\s*percent\s*\}\}/gi, formatPercent(percent)),
        href: offers.length === 1 ? safeBannerDestination(offers[0].bannerDestinationUrl) || fallback : fallback }];
      var seen = new Set();
      message.textContent = '';
      entries.forEach(function (entry) {
        var key = entry.text + '\n' + entry.href;
        if (seen.has(key)) return;
        seen.add(key);
        if (message.childNodes.length) message.appendChild(document.createTextNode(' ? '));
        var link = document.createElement('a');
        link.className = 'revlift-sale-banner__offer-link link focus-inset';
        link.href = entry.href;
        link.textContent = entry.text;
        message.appendChild(link);
      });
    });
  }

  function syncSaleBanner() {
    var banners = Array.from(document.querySelectorAll('[data-revlift-sale-banner]'));
    var standardAnnouncements = Array.from(document.querySelectorAll('[data-revlift-standard-announcement]'));
    fetchConfig('', { fresh: true }).then(function (config) {
      var active = hasStoreWideDiscount(config);
      activeConfig = config;
      membershipRequests.clear();
      storeWidePercent = active ? storeWideDiscountPercent(config) : 0;
      window.RevLiftPricing.storeWideActive = active;
      window.RevLiftPricing.storeWidePercent = storeWidePercent;
      syncSaleBannerText(banners, config);
      banners.forEach(function (banner) { banner.hidden = !active; });
      standardAnnouncements.forEach(function (announcement) { announcement.hidden = active; });
      document.dispatchEvent(new CustomEvent('revlift:storewide-state', {
        detail: { active: active, discountPercent: storeWidePercent }
      }));
      syncStoreWidePricing();
      refreshPurchaseOptionForms();
      observeDynamicPrices();
    }).catch(function () {
      activeConfig = null;
      storeWidePercent = 0;
      window.RevLiftPricing.storeWideActive = false;
      window.RevLiftPricing.storeWidePercent = 0;
      banners.forEach(function (banner) { banner.hidden = true; });
      standardAnnouncements.forEach(function (announcement) { announcement.hidden = false; });
      document.dispatchEvent(new CustomEvent('revlift:storewide-state', { detail: { active: false } }));
      syncStoreWidePricing();
      observeDynamicPrices();
    });
  }

  window.RevLiftPricing = {
    fetchConfig: fetchConfig,
    quote: quote,
    hasDiscount: offerHasDiscount,
    hasStoreWideDiscount: hasStoreWideDiscount,
    storeWideDiscountPercent: storeWideDiscountPercent,
    activeStoreWidePercent: activeStoreWidePercent,
    bestDiscountPercent: bestDiscountPercent,
    bestDiscountedTotal: bestDiscountedTotal,
    discountedTotal: discountedTotal,
    syncStoreWidePricing: syncStoreWidePricing,
    config: function () { return activeConfig; },
    storeWideActive: null,
    storeWidePercent: 0
  };

  document.addEventListener('revlift:refresh', function (event) {
    var form = event.target && event.target.closest ? event.target.closest('form[data-revlift-base-total-cents]') : null;
    var fromStoreWideSync = event.detail && event.detail.source === 'storewide-pricing';
    if (form) syncPurchaseOptionForm(form, !fromStoreWideSync);
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncSaleBanner, { once: true });
  } else {
    syncSaleBanner();
  }
  document.dispatchEvent(new CustomEvent('revlift:pricing-ready'));
})();
